import React, { createContext, RefObject, useContext, useRef } from "react";
import * as styles from "./cascading-panel-stack.module.scss";
import { classArr } from "../helper/style-helper";
import { createPortal } from "react-dom";

interface CascadingPanelStackContextProps {
	element: RefObject<HTMLDivElement | null>;
}

const CascadingPanelStackContext =
	createContext<CascadingPanelStackContextProps>({
		element: { current: null },
	});

export interface CascadingPanelStackProviderProps {
	className?: string;
	children?: React.ReactNode;
}

export function CascadingPanelStackProvider(
	props: CascadingPanelStackProviderProps,
) {
	const ref = useRef<HTMLDivElement | null>(null);

	return (
		<div className={classArr(props.className, styles.wrapper)} ref={ref}>
			<CascadingPanelStackContext value={{ element: ref }}>
				{props.children}
			</CascadingPanelStackContext>
		</div>
	);
}

export interface CascadingPanelStackPortalProps {
	children?: React.ReactNode;
}

export function CascadingPanelStackPortal(
	props: CascadingPanelStackPortalProps,
) {
	const context = useContext(CascadingPanelStackContext);
	return createPortal(
		<div className={styles.portal}>{props.children}</div>,
		context.element.current!,
	);
}

export interface PinnedCascadingPanelStackPortalProps {
	refPosition: HTMLElement | null;
	children?: React.ReactNode;
}

export function PinnedCascadingPanelStackPortal(
	props: PinnedCascadingPanelStackPortalProps,
) {
	const context = useContext(CascadingPanelStackContext);
	const parentRect = context.element.current!.getBoundingClientRect();
	const anchorRect = props.refPosition?.getBoundingClientRect();

	if (anchorRect === undefined) {
		return null;
	}

	const offsetTop = anchorRect.bottom - parentRect.top;

	return createPortal(
		<div style={{ top: offsetTop }} className={styles.portal}>
			{props.children}
		</div>,
		context.element.current!,
	);
}
